import React, { useState, useEffect, useRef } from 'react';
import { PostRequest, GetRequest } from 'components/common/index.js';
import { getThemeDefaultHighlightColor } from 'components/quote_builder_widget/themes';
import WidgetPreview from 'components/quote_builder_widget/WidgetPreview';
import CssEditorModal from 'components/quote_builder_widget/CssEditorModal';
import SettingsTabs, { tabIdFor } from 'components/quote_builder_widget/SettingsTabs';
import { ConfigurationSection, AppearanceSection, MaterialsMenuSection, PrintingMachinesSection, InstallationSection } from 'components/quote_builder_widget/sections';
import styles from 'components/quote_builder_widget/settings/_style.module.scss';

const SAMPLE_TEMPLATE_IDENTIFIER = 'becf-12017';

const VALID_SECTIONS = ['configuration', 'appearance', 'carton_materials', 'corrugated_materials', 'printing', 'installation'];

const getSectionFromHash = () => {
  const hash = window.location.hash.replace('#', '');
  if (VALID_SECTIONS.includes(hash)) {
    return hash;
  }
  return 'configuration';
};

// Sample drawings available for preview, organized by main group
const PREVIEW_DRAWINGS = [
  { value: 'becf-12017', label: 'Cartons | Food Boxes | becf-12017 ', materialSection: 'carton_materials' },
  { value: 'becf-10803', label: 'Cartons | Tuck End Boxes | becf-10803', materialSection: 'carton_materials' },
  { value: 'becf-21712', label: 'Corrugated Cardboards | Tray Boxes | becf-21712', materialSection: 'corrugated_materials' },
  { value: 'becf-30101', label: 'Hard Cardboards | Covered Solid Board Boxes | becf-30101', materialSection: null },
];

// Themes that support custom CSS
const THEMES_WITH_CUSTOM_CSS = ['dark', 'default', 'minimal', 'compact'];

// Map user's default unit to widget unit option
const getDefaultUnitOption = (defaultUnit) => {
  if (defaultUnit === 'in') {
    return 'in';
  }
  return 'mm';
};

const FormNew = ({ identifier, setMode, defaultUnit = 'mm', defaultLanguageOption = 'en', widgetHost }) => {
  const initialFetchDone = useRef(false);
  const [activeSection, setActiveSection] = useState(getSectionFromHash());
  const [domains, setDomains] = useState([]);
  const [selectedTheme, setSelectedTheme] = useState('default');
  const [highlightColor, setHighlightColor] = useState(getThemeDefaultHighlightColor('default'));
  const [submitButtonColor, setSubmitButtonColor] = useState('');
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const previousThemeRef = useRef('default');
  const [fontFamily, setFontFamily] = useState('');
  const [unitOption, setUnitOption] = useState(identifier ? 'both' : getDefaultUnitOption(defaultUnit));
  const [languageOption, setLanguageOption] = useState(defaultLanguageOption);
  const [customCss, setCustomCss] = useState('');
  const customCssRef = useRef('');
  const [customCssByTheme, setCustomCssByTheme] = useState({});
  const customCssByThemeRef = useRef({});
  const [isCssEditorOpen, setIsCssEditorOpen] = useState(false);

  const [addDomainFormVisible, setAddDomainFormVisible] = useState(false);
  const [addDomainFormHighlighted, setAddDomainFormHighlighted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [newDomain, setNewDomain] = useState('');
  const [savedIdentifier, setSavedIdentifier] = useState(identifier);
  const [previewKey, setPreviewKey] = useState(Date.now());
  const [previewDrawing, setPreviewDrawing] = useState(SAMPLE_TEMPLATE_IDENTIFIER);

  const supportsCustomCss = THEMES_WITH_CUSTOM_CSS.includes(selectedTheme);

  // Send style updates to widget preview iframe for live preview
  const sendStyleUpdate = (type, data) => {
    const iframes = document.querySelectorAll('iframe');
    iframes.forEach((iframe) => {
      try {
        iframe.contentWindow.postMessage({ type, ...data }, '*');
      } catch (error) {
        // Ignore cross-origin errors
      }
    });
  };

  useEffect(() => {
    if (supportsCustomCss && savedIdentifier) {
      sendStyleUpdate('diecuttemplates.update_background_color', { color: backgroundColor });
    }
  }, [backgroundColor, supportsCustomCss, savedIdentifier]);

  useEffect(() => {
    if (supportsCustomCss && savedIdentifier) {
      sendStyleUpdate('diecuttemplates.update_font_family', { fontFamily: fontFamily });
    }
  }, [fontFamily, supportsCustomCss, savedIdentifier]);

  useEffect(() => {
    if (supportsCustomCss && savedIdentifier) {
      sendStyleUpdate('diecuttemplates.update_highlight_color', { color: highlightColor });
    }
  }, [highlightColor, supportsCustomCss, savedIdentifier]);

  useEffect(() => {
    if (supportsCustomCss && savedIdentifier) {
      sendStyleUpdate('diecuttemplates.update_submit_button_color', { color: submitButtonColor || highlightColor });
    }
  }, [submitButtonColor, highlightColor, supportsCustomCss, savedIdentifier]);

  useEffect(() => {
    customCssRef.current = customCss;
  }, [customCss]);

  useEffect(() => {
    customCssByThemeRef.current = customCssByTheme;
  }, [customCssByTheme]);

  // Update highlight color and switch to theme-specific CSS when theme changes
  useEffect(() => {
    if (!initialFetchDone.current) {
      return;
    }
    const previousTheme = previousThemeRef.current;
    const previousDefault = getThemeDefaultHighlightColor(previousTheme);
    const newDefault = getThemeDefaultHighlightColor(selectedTheme);

    if (highlightColor === previousDefault && previousDefault !== newDefault) {
      setHighlightColor(newDefault);
    }

    if (previousTheme !== selectedTheme) {
      setCustomCssByTheme((previous) => ({
        ...previous,
        [previousTheme]: customCssRef.current
      }));
      setCustomCss(customCssByThemeRef.current[selectedTheme] || '');
    }

    previousThemeRef.current = selectedTheme;
  }, [selectedTheme]);

  const handleSectionChange = (section) => {
    setActiveSection(section);
    window.history.pushState(null, '', `#${section}`);
  };

  useEffect(() => {
    const previewMaterialSection = PREVIEW_DRAWINGS.find((drawing) => drawing.value === previewDrawing)?.materialSection;
    const sampleForSection = PREVIEW_DRAWINGS.find((drawing) => drawing.materialSection === activeSection);
    if (!sampleForSection || previewMaterialSection === activeSection) {
      return;
    }
    setPreviewDrawing(sampleForSection.value);
    setPreviewKey(Date.now());
  }, [activeSection]);

  useEffect(() => {
    const onPopState = () => {
      setActiveSection(getSectionFromHash());
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const fetchWidget = () => {
    if (identifier === undefined) {
      return;
    }

    GetRequest({
      url: `/quote-builder/user-quote-builder-widgets/${identifier}`,
      callback: (data) => {
        const theme = data.properties.theme || 'default';
        setDomains(data.domains);
        setSelectedTheme(theme);
        setHighlightColor(data.properties.highlight_color || getThemeDefaultHighlightColor(theme));
        setSubmitButtonColor(data.properties.submit_button_color || '');
        setBackgroundColor(data.properties.background_color || '#ffffff');
        setFontFamily(data.properties.font_family || '');
        setUnitOption(data.properties.unit_option || 'both');
        setLanguageOption(data.language_option);
        setCustomCss(data.custom_css || '');
        customCssRef.current = data.custom_css || '';
        setCustomCssByTheme(data.custom_css_by_theme || {});
        customCssByThemeRef.current = data.custom_css_by_theme || {};
        previousThemeRef.current = theme;
        setTimeout(() => {
          initialFetchDone.current = true;
        }, 100);
      }
    });
  };

  const handleCancel = (event) => {
    event.preventDefault();
    setMode();
  };

  const saveWidget = (overrides = {}) => {
    const domainsToSave = overrides.domains !== undefined ? overrides.domains : domains;

    const saveData = {
      identifier: savedIdentifier,
      domains: domainsToSave,
      theme: selectedTheme,
    };

    if (supportsCustomCss) {
      saveData.highlight_color = highlightColor;
      saveData.submit_button_color = submitButtonColor;
      saveData.background_color = backgroundColor;
      saveData.font_family = fontFamily;
      saveData.custom_css = overrides.customCss !== undefined ? overrides.customCss : customCss;
    }

    saveData.unit_option = unitOption;
    saveData.language_option = languageOption;

    PostRequest({
      url: '/quote-builder/user-quote-builder-widgets',
      data: saveData,
      callback: (data) => {
        setSuccessMessage(t('quote_builder_widget.saved_successfully'));
        setPreviewKey(Date.now());
        if (!savedIdentifier) {
          setSavedIdentifier(data.identifier);
          window.history.replaceState({}, '', `/quote-builder/widgets/${data.identifier}/edit`);
        }
      },
      400: (data) => {
        setErrorMessage(data.error);
      }
    });
  };

  // Auto-save when domains change (after initial fetch)
  useEffect(() => {
    if (!initialFetchDone.current) {
      return;
    }
    if (domains.length === 0) {
      return;
    }
    saveWidget();
  }, [domains]);

  const handleSave = (event) => {
    event.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    if (domains.length === 0) {
      handleSectionChange('configuration');
      setAddDomainFormVisible(true);
      setAddDomainFormHighlighted(true);
      return;
    }

    saveWidget();
  };

  useEffect(fetchWidget, [identifier]);

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'configuration':
        return (
          <ConfigurationSection
            domains={domains}
            setDomains={setDomains}
            unitOption={unitOption}
            setUnitOption={setUnitOption}
            languageOption={languageOption}
            setLanguageOption={setLanguageOption}
            addDomainFormVisible={addDomainFormVisible}
            setAddDomainFormVisible={setAddDomainFormVisible}
            addDomainFormHighlighted={addDomainFormHighlighted}
            setAddDomainFormHighlighted={setAddDomainFormHighlighted}
            newDomain={newDomain}
            setNewDomain={setNewDomain}
          />
        );
      case 'appearance':
        return (
          <AppearanceSection
            selectedTheme={selectedTheme}
            setSelectedTheme={setSelectedTheme}
            highlightColor={highlightColor}
            setHighlightColor={setHighlightColor}
            submitButtonColor={submitButtonColor}
            setSubmitButtonColor={setSubmitButtonColor}
            backgroundColor={backgroundColor}
            setBackgroundColor={setBackgroundColor}
            fontFamily={fontFamily}
            setFontFamily={setFontFamily}
            customCss={customCss}
            supportsCustomCss={supportsCustomCss}
            onOpenCssEditor={() => setIsCssEditorOpen(true)}
          />
        );
      case 'carton_materials':
      case 'corrugated_materials':
        return (
          <MaterialsMenuSection
            activeSection={activeSection}
            onSectionChange={handleSectionChange}
            onMaterialsChange={() => setPreviewKey(Date.now())}
          />
        );
      case 'printing':
        return <PrintingMachinesSection onPrintingMachinesChange={() => setPreviewKey(Date.now())} />;
      case 'installation':
        return (
          <InstallationSection
            widgetHost={widgetHost}
            identifier={savedIdentifier}
            domains={domains}
            onSectionChange={handleSectionChange}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div>
      {/* Error Message */}
      {errorMessage !== '' && (
        <div className={`alert alert-danger fade in ${styles.errorAlert}`}>
          <strong><i className="fa fa-exclamation-circle"></i> {errorMessage}</strong>
        </div>
      )}

      {/* Main Layout */}
      <div className={styles.pageWrapper}>
        {/* Header: back link and tabs in one row */}
        <div className={styles.header}>
          <a href="/quote-builder/widgets" className={styles.backLink}>
            <i className="fa fa-arrow-left"></i> {t('quote_builder_widget.back')}
          </a>
          <SettingsTabs
            activeSection={activeSection}
            onSectionChange={handleSectionChange}
          />
        </div>

        {/* Content + Preview */}
        <div className={styles.container}>
          {/* Content Area */}
          <div
            className={styles.contentPanel}
            role="tabpanel"
            id={`tabpanel-${tabIdFor(activeSection)}`}
            aria-labelledby={`tab-${tabIdFor(activeSection)}`}
          >
            {renderActiveSection()}
            {!['materials', 'printing', 'installation'].includes(tabIdFor(activeSection)) && (
              <div className={styles.footerActions}>
                {successMessage !== '' && (
                  <span className={styles.savedMessage}><i className="fa fa-check-circle"></i> {successMessage}</span>
                )}
                <button className="dct-button dct-button--primary" type="button" onClick={handleSave}>
                  {t('quote_builder_widget.save')}
                </button>
                <button className="dct-button dct-button--secondary" type="button" onClick={handleCancel}>
                  {t('quote_builder_widget.cancel')}
                </button>
              </div>
            )}
          </div>

          {/* Preview Panel */}
          {savedIdentifier && (
            <div className={styles.previewContainer}>
              <center>
                <h5 className={styles.previewTitle}>{t('quote_builder_widget.preview')}</h5>
                <div className={styles.previewDropdownContainer}>
                  <select
                    className={`form-control ${styles.previewSelect}`}
                    aria-label={t('quote_builder_widget.preview_drawing')}
                    value={previewDrawing}
                    onChange={(event) => {
                      setPreviewDrawing(event.target.value);
                      setPreviewKey(Date.now());
                    }}
                  >
                    {PREVIEW_DRAWINGS.map((drawing) => (
                      <option key={drawing.value} value={drawing.value}>
                        {drawing.label}
                      </option>
                    ))}
                  </select>
                </div>
                <hr className={styles.previewSeparator} />
                <div className={styles.previewBox}>
                  <WidgetPreview
                    identifier={savedIdentifier}
                    templateIdentifier={previewDrawing}
                    previewKey={previewKey}
                  />
                </div>
              </center>
            </div>
          )}
        </div>
      </div>

      {supportsCustomCss && (
        <CssEditorModal
          isOpen={isCssEditorOpen}
          onClose={() => setIsCssEditorOpen(false)}
          onSave={(newCss) => {
            setCustomCss(newCss);
            setCustomCssByTheme((previous) => ({
              ...previous,
              [selectedTheme]: newCss
            }));
            setIsCssEditorOpen(false);
            saveWidget({ customCss: newCss });
          }}
          currentCss={customCss}
          theme={selectedTheme}
          domains={domains}
        />
      )}
    </div>
  );
};

export default FormNew;
